import { ErrorResponse, UserManager, type User as OidcUser } from 'oidc-client-ts'
import { REDIRECT_IN_PROGRESS, type AuthProvider, type AuthSession, type MfaProvider } from './AuthProvider'
import { NoInAppMfa, unsupported } from './NoInAppMfa'
import type { User } from '@/types/user'
import { getLoginPath } from '@/utils/domain'

export interface KeycloakSettings {
  /** The realm's issuer, e.g. `https://auth.scrivio.nl/realms/scrivio`. */
  authority: string
  clientId: string
}

/** Keycloak's `kc_idp_hint` aliases for the SSO buttons the login page offers. */
const IDENTITY_PROVIDER_HINTS: Record<string, string> = {
  google: 'google',
  microsoft: 'microsoft',
}

/**
 * Token-endpoint errors after which no refresh can recover the session:
 * the refresh token is expired or revoked, or the SSO session is gone.
 */
const TERMINAL_OIDC_ERRORS = ['invalid_grant', 'login_required']

/**
 * Sign-in against a Keycloak realm with the authorization-code flow and PKCE
 * (ADR-045). Every interactive step happens on Keycloak's pages: the app sends
 * the person there and reads the result back in {@link handleRedirectResult},
 * so a password never passes through the app.
 */
export class KeycloakAuthService implements AuthProvider {
  readonly mfa: MfaProvider = new NoInAppMfa()
  private readonly userManager: UserManager
  /** The last user oidc-client-ts loaded, so {@link currentSession} can answer synchronously. */
  private user: OidcUser | null = null
  private readonly ready: Promise<void>

  constructor(settings: KeycloakSettings) {
    if (!settings.authority || !settings.clientId) {
      throw new Error('KeycloakAuthService needs an authority and a client id')
    }
    const loginUrl = window.location.origin + getLoginPath()
    // No userStore: the library's sessionStorage default keeps tokens out of localStorage.
    this.userManager = new UserManager({
      authority: settings.authority,
      client_id: settings.clientId,
      redirect_uri: loginUrl,
      post_logout_redirect_uri: loginUrl,
      response_type: 'code',
      // What both realms' clients grant (keycloak/realms): Keycloak refuses the whole sign-in
      // with `invalid_scope` for one scope more, and the name comes from the backend instead.
      scope: 'openid email',
    })
    this.userManager.events.addUserLoaded((user) => {
      this.user = user
    })
    this.userManager.events.addUserUnloaded(() => {
      this.user = null
    })
    this.ready = this.userManager
      .getUser()
      .then((user) => {
        this.user = user
      })
      .catch(() => {
        // An unreadable stored session is no session; the next sign-in replaces it.
        this.user = null
      })
  }

  private mapUser(user: OidcUser | null): User | null {
    if (!user || user.expired) return null
    return {
      uid: user.profile.sub,
      email: user.profile.email ?? null,
      displayName: user.profile.name ?? null,
      emailVerified: user.profile.email_verified ?? false,
    }
  }

  private async redirectToKeycloak(extraQueryParams: Record<string, string>, loginHint?: string): Promise<never> {
    await this.userManager.signinRedirect({ login_hint: loginHint, extraQueryParams })
    throw new Error(REDIRECT_IN_PROGRESS)
  }

  /** Keycloak asks for the password on its own page; `email`, when there is one, pre-fills it there. */
  async signIn(email: string): Promise<User> {
    return this.redirectToKeycloak({}, email || undefined)
  }

  async signUp(): Promise<User> {
    throw unsupported('Self-service sign-up')
  }

  async signInWithSSO(provider: string, loginHint?: string): Promise<User> {
    const hint = IDENTITY_PROVIDER_HINTS[provider.toLowerCase()]
    if (!hint) {
      throw new Error(`Unsupported SSO provider: ${provider}`)
    }
    return this.redirectToKeycloak({ kc_idp_hint: hint }, loginHint)
  }

  /** Ends the Keycloak session too, so the next sign-in asks for credentials again. */
  async signOut(): Promise<void> {
    await this.userManager.signoutRedirect()
  }

  async getCurrentUser(): Promise<User | null> {
    await this.ready
    return this.mapUser(this.user)
  }

  onAuthStateChanged(callback: (user: User | null) => void): () => void {
    const stopLoaded = this.userManager.events.addUserLoaded((user) => callback(this.mapUser(user)))
    const stopUnloaded = this.userManager.events.addUserUnloaded(() => callback(null))
    void this.getCurrentUser().then(callback)
    return () => {
      stopLoaded()
      stopUnloaded()
    }
  }

  /** Completes a sign-in Keycloak sent back to the login page; `null` when there is none. */
  async handleRedirectResult(): Promise<User | null> {
    const url = new URL(window.location.href)
    if (!url.searchParams.has('state') || !(url.searchParams.has('code') || url.searchParams.has('error'))) {
      return null
    }
    const user = await this.userManager.signinRedirectCallback()
    url.searchParams.delete('code')
    url.searchParams.delete('state')
    url.searchParams.delete('session_state')
    url.searchParams.delete('iss')
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
    return this.mapUser(user)
  }

  currentSession(): AuthSession | null {
    const user = this.mapUser(this.user)
    if (!user) return null
    return { uid: user.uid, email: user.email, emailVerified: user.emailVerified ?? false }
  }

  private async freshUser(forceRefresh: boolean): Promise<OidcUser | null> {
    await this.ready
    if (!this.user) return null
    if (forceRefresh || this.user.expired) {
      return await this.userManager.signinSilent()
    }
    return this.user
  }

  async getIdToken(forceRefresh = false): Promise<string | null> {
    return (await this.freshUser(forceRefresh))?.access_token ?? null
  }

  /** The ID token's claims: the access token is the API's to read, not the app's. */
  async getTokenClaims(forceRefresh = false): Promise<Record<string, unknown> | null> {
    return (await this.freshUser(forceRefresh))?.profile ?? null
  }

  isSessionExpiredError(error: unknown): boolean {
    return error instanceof ErrorResponse && error.error !== null && TERMINAL_OIDC_ERRORS.includes(error.error)
  }

  async sendEmailVerification(): Promise<void> {
    throw unsupported('Sending a verification e-mail from the app')
  }

  async signInWithCustomToken(): Promise<void> {
    throw unsupported('Signing in with a custom token')
  }

  isSignInLink(): boolean {
    return false
  }

  async signInWithEmailLink(): Promise<void> {
    throw unsupported('Signing in with an e-mail link')
  }
}
